import React, { useEffect, useState } from "react"; // Asegúrate de importar useState
import { Checkbox, Col, DatePicker, Select, Typography } from "antd";
import moment from "moment";
import { Control, Controller } from "react-hook-form";
import useFilter from "../../hooks/students/useFilterStudents";
import {
  CaUserAcademy,
  ProgramScheduleType,
  ProgramStudentType,
} from "../../../../types/dto/program";
import { useUsers } from "../../../users/hooks/useUsers";
import { usersService } from "../../../../shared/services";
import { formatNumberPrice } from "../../../../shared/utils/Helper";

const { Text } = Typography;

const useDebounce = (cb: () => void, deps: any[], delay = 500) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      cb();
    }, delay);
    return () => clearTimeout(timeout);
  }, deps);
};

export default function AssignedStudentProgramForm({
  control,
  programId,
  programScheduleByProgram,
  setSelectedSchedules,
}: {
  control: Control<ProgramStudentType>;
  programId?: number;
  programScheduleByProgram?: ProgramScheduleType[];
  setSelectedSchedules?: React.Dispatch<
    React.SetStateAction<{ id: number; price: number }[]>
  >;
}) {
  const [studentSearchValue, setStudentSearchValue] = useState("");
  const { studentWithoutPrograms, studentWithoutProgramsQuery } = useUsers();

  // Estado local para manejar los checkboxes
  const [checkedSchedules, setCheckedSchedules] = useState<
    { id: number; price: number }[]
  >([]);

  useEffect(() => {
    if (programId) {
      studentWithoutProgramsQuery(+programId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const clientQuery = React.useMemo(
    () => ({ studentWithoutPrograms: programId }),
    [programId]
  );

  const { setQuery, filteredData, loading } = useFilter<CaUserAcademy>({
    service: usersService,
    initialData: studentWithoutPrograms,
    student_teacher: "student",
    clientQuery,
  });

  useDebounce(() => {
    setQuery(studentSearchValue);
  }, [studentSearchValue]);

  useEffect(() => {
    if (programScheduleByProgram) {
      // Inicializa el estado de los checkboxes marcándolos por defecto
      const initialCheckedSchedules = programScheduleByProgram.map((item) => ({
        id: item.id,
        price: +item.price_with_prompt_payment_discount,
      }));
      setCheckedSchedules(initialCheckedSchedules);
      if (setSelectedSchedules) {
        setSelectedSchedules(initialCheckedSchedules); // También actualiza el estado externo si es necesario
      }
    }
  }, [programScheduleByProgram, setSelectedSchedules]);

  const handleCheckboxChange = (
    scheduleId: number,
    price: number,
    isChecked: boolean
  ) => {
    if (setSelectedSchedules) {
      setSelectedSchedules((prev) =>
        isChecked
          ? [...prev, { id: scheduleId, price }]
          : prev.filter((schedule) => schedule.id !== scheduleId)
      );
    }
    // Actualiza el estado local de los checkboxes
    setCheckedSchedules((prev) =>
      isChecked
        ? [...prev, { id: scheduleId, price }]
        : prev.filter((schedule) => schedule.id !== scheduleId)
    );
  };

  const uniqueStudents = React.useMemo(
    () =>
      Array.from(
        new Map(filteredData.map((student) => [student.id, student])).values()
      ),
    [filteredData]
  );

  return (
    <>
      {programScheduleByProgram && programScheduleByProgram.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div>
            <label>
              <strong>Selecciona un estudiante</strong>
            </label>
            <Controller
              control={control}
              name="user_id"
              render={({ field, fieldState }) => (
                <div>
                  <Select
                    {...field}
                    showSearch
                    style={{ width: "100%", height: 30 }}
                    placeholder="Selecciona un estudiante"
                    optionFilterProp="label"
                    onSearch={(value) => setStudentSearchValue(value)}
                    filterOption={false}
                    loading={loading}
                    options={
                      uniqueStudents.length > 0
                        ? uniqueStudents.map((it) => ({
                            key: `${it.id}`,
                            value: `${it.id}`,
                            label: `${it.first_name} ${it.last_name} - ${it.student_code}`,
                          }))
                        : [
                            {
                              key: "",
                              value: "",
                              label: "No hay estudiantes disponibles",
                            },
                          ]
                    }
                  />
                  {fieldState.error ? (
                    <span className="mt-1 block text-xs text-red-500">
                      {fieldState.error.message}
                    </span>
                  ) : null}
                </div>
              )}
            />
          </div>
          <div>
            <label>
              <strong>Fecha de matricula</strong>
            </label>
            <Controller
              control={control}
              name="enrollment_date"
              render={({ field, fieldState }) => (
                <div>
                  <DatePicker
                    {...field}
                    value={field.value ? moment(field.value) : null}
                    onChange={(date) =>
                      field.onChange(date?.format("YYYY-MM-DD"))
                    }
                    style={{ width: "100%", height: 30 }}
                    placeholder="Selecciona una fecha"
                    format="DD/MM/YYYY"
                    showTime={false}
                  />
                  {fieldState.error ? (
                    <span className="mt-1 block text-xs text-red-500">
                      {fieldState.error.message}
                    </span>
                  ) : null}
                </div>
              )}
            />
          </div>
          {programScheduleByProgram && (
            <div className="mt-3">
              <label>
                <strong>Asignación del cronograma de pago al estudiante</strong>
              </label>
              {programScheduleByProgram.map((item) => (
                <Col
                  key={item.id}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 15,
                  }}
                >
                  <Checkbox
                    checked={checkedSchedules.some(
                      (schedule) => schedule.id === item.id
                    )} // Marca el checkbox si está en el estado
                    onChange={(e) =>
                      handleCheckboxChange(
                        item.id,
                        +item.price_with_prompt_payment_discount,
                        e.target.checked
                      )
                    }
                  >
                    <strong>
                      {moment(item.month).format("MMMM YYYY").toUpperCase()}
                    </strong>
                    :{" "}
                    {formatNumberPrice(
                      +item.price_with_prompt_payment_discount
                    )}
                  </Checkbox>
                </Col>
              ))}
            </div>
          )}
        </div>
      ) : (
        <Text>Primero debes crear el cronograma de pago del programa</Text>
      )}
    </>
  );
}
